import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { lngLatToWorld } from '@math.gl/web-mercator';
var TILE_SIZE = 512;
export var urlType = {
  type: 'url',
  value: '',
  validate: function validate(value) {
    return typeof value === 'string' || Array.isArray(value) && value.every(function (url) {
      return typeof url === 'string';
    });
  },
  equals: function equals(value1, value2) {
    if (value1 === value2) {
      return true;
    }

    if (!Array.isArray(value1) || !Array.isArray(value2)) {
      return false;
    }

    var len = value1.length;

    if (len !== value2.length) {
      return false;
    }

    for (var i = 0; i < len; i++) {
      if (value1[i] !== value2[i]) {
        return false;
      }
    }

    return true;
  }
};
export function getURLFromTemplate(template, properties) {
  if (!template || !template.length) {
    return null;
  }

  if (Array.isArray(template)) {
    var index = Math.abs(properties.x + properties.y) % template.length;
    template = template[index];
  }

  return template.replace(/\{ *([\w_-]+) *\}/g, function (_, property) {
    return properties[property];
  });
}

function getBoundingBox(viewport, zRange) {
  var corners;

  if (zRange && zRange.length === 2) {
    var _zRange = _slicedToArray(zRange, 2),
        minZ = _zRange[0],
        maxZ = _zRange[1];

    var minTargetZ = {
      targetZ: minZ
    };
    var maxTargetZ = {
      targetZ: maxZ
    };
    corners = [viewport.unproject([0, 0], minTargetZ), viewport.unproject([viewport.width, 0], minTargetZ), viewport.unproject([0, viewport.height], minTargetZ), viewport.unproject([viewport.width, viewport.height], minTargetZ), viewport.unproject([0, 0], maxTargetZ), viewport.unproject([viewport.width, 0], maxTargetZ), viewport.unproject([0, viewport.height], maxTargetZ), viewport.unproject([viewport.width, viewport.height], maxTargetZ)];
  } else {
    corners = [viewport.unproject([0, 0]), viewport.unproject([viewport.width, 0]), viewport.unproject([0, viewport.height]), viewport.unproject([viewport.width, viewport.height])];
  }

  return [Math.min.apply(Math, _toConsumableArray(corners.map(function (arr) {
    return arr[0];
  }))), Math.min.apply(Math, _toConsumableArray(corners.map(function (arr) {
    return arr[1];
  }))), Math.max.apply(Math, _toConsumableArray(corners.map(function (arr) {
    return arr[0];
  }))), Math.max.apply(Math, _toConsumableArray(corners.map(function (arr) {
    return arr[1];
  })))];
}

function getOSMTileIndex(lngLat, scale) {
  var _lngLatToWorld = lngLatToWorld(lngLat),
      _lngLatToWorld2 = _slicedToArray(_lngLatToWorld, 2),
      x = _lngLatToWorld2[0],
      y = _lngLatToWorld2[1];

  x *= scale / TILE_SIZE;
  y = (1 - y / TILE_SIZE) * scale;
  return [x, y];
}

function getTileIndex(_ref, scale) {
  var _ref2 = _slicedToArray(_ref, 2),
      x = _ref2[0],
      y = _ref2[1];

  return [x * scale / TILE_SIZE, y * scale / TILE_SIZE];
}

function getScale(z) {
  var tileSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : TILE_SIZE;
  return Math.pow(2, z) * TILE_SIZE / tileSize;
}

function osmTile2lngLat(x, y, z) {
  var scale = getScale(z);
  var lng = x / scale * 360 - 180;
  var n = Math.PI - 2 * Math.PI * y / scale;
  var lat = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return [lng, lat];
}

function tile2XY(x, y, z, tileSize) {
  var scale = getScale(z, tileSize);
  return [x / scale * TILE_SIZE, y / scale * TILE_SIZE];
}

export function tileToBoundingBox(viewport, x, y, z) {
  var tileSize = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : TILE_SIZE;

  if (viewport.isGeospatial) {
    var _osmTile2lngLat = osmTile2lngLat(x, y, z),
        _osmTile2lngLat2 = _slicedToArray(_osmTile2lngLat, 2),
        west = _osmTile2lngLat2[0],
        north = _osmTile2lngLat2[1];

    var _osmTile2lngLat3 = osmTile2lngLat(x + 1, y + 1, z),
        _osmTile2lngLat4 = _slicedToArray(_osmTile2lngLat3, 2),
        east = _osmTile2lngLat4[0],
        south = _osmTile2lngLat4[1];

    return {
      west: west,
      north: north,
      east: east,
      south: south
    };
  }

  var _tile2XY = tile2XY(x, y, z, tileSize),
      _tile2XY2 = _slicedToArray(_tile2XY, 2),
      left = _tile2XY2[0],
      top = _tile2XY2[1];

  var _tile2XY3 = tile2XY(x + 1, y + 1, z, tileSize),
      _tile2XY4 = _slicedToArray(_tile2XY3, 2),
      right = _tile2XY4[0],
      bottom = _tile2XY4[1];

  return {
    left: left,
    top: top,
    right: right,
    bottom: bottom
  };
}

function getIdentityTileIndices(viewport, z, tileSize) {
  var bbox = getBoundingBox(viewport);
  var scale = getScale(z, tileSize);

  var _getTileIndex = getTileIndex([bbox[0], bbox[1]], scale),
      _getTileIndex2 = _slicedToArray(_getTileIndex, 2),
      minX = _getTileIndex2[0],
      minY = _getTileIndex2[1];

  var _getTileIndex3 = getTileIndex([bbox[2], bbox[3]], scale),
      _getTileIndex4 = _slicedToArray(_getTileIndex3, 2),
      maxX = _getTileIndex4[0],
      maxY = _getTileIndex4[1];

  var indices = [];

  for (var x = Math.floor(minX); x < maxX; x++) {
    for (var y = Math.floor(minY); y < maxY; y++) {
      indices.push({
        x: x,
        y: y,
        z: z
      });
    }
  }

  return indices;
}

function getOSMTileIndices(viewport, z, zRange) {
  var bbox = getBoundingBox(viewport, zRange);
  var scale = getScale(z);

  var _getOSMTileIndex = getOSMTileIndex([bbox[0], bbox[3]], scale),
      _getOSMTileIndex2 = _slicedToArray(_getOSMTileIndex, 2),
      minX = _getOSMTileIndex2[0],
      minY = _getOSMTileIndex2[1];

  var _getOSMTileIndex3 = getOSMTileIndex([bbox[2], bbox[1]], scale),
      _getOSMTileIndex4 = _slicedToArray(_getOSMTileIndex3, 2),
      maxX = _getOSMTileIndex4[0],
      maxY = _getOSMTileIndex4[1];

  var indices = [];
  minX = Math.floor(minX);
  maxX = Math.min(minX + scale, maxX);
  minY = Math.max(0, Math.floor(minY));
  maxY = Math.min(scale, maxY);

  for (var x = minX; x < maxX; x++) {
    for (var y = minY; y < maxY; y++) {
      var normalizedX = x - Math.floor(x / scale) * scale;
      indices.push({
        x: normalizedX,
        y: y,
        z: z
      });
    }
  }

  return indices;
}

export function getTileIndices(viewport, maxZoom, minZoom, zRange) {
  var tileSize = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : TILE_SIZE;
  var z = Math.ceil(viewport.zoom);

  if (Number.isFinite(minZoom) && z < minZoom) {
    return [];
  }

  if (Number.isFinite(maxZoom) && z > maxZoom) {
    z = maxZoom;
  }

  return viewport.isGeospatial ? getOSMTileIndices(viewport, z, zRange) : getIdentityTileIndices(viewport, z, tileSize);
}
//# sourceMappingURL=utils.js.map